import { QueryClient } from "@tanstack/react-query";
import { createTRPCReact } from "@trpc/react-query";
import {
  httpBatchLink,
  httpSubscriptionLink,
  retryLink,
  splitLink,
  TRPCClientError,
  TRPCLink,
} from "@trpc/client";
import { observable } from "@trpc/server/observable";
import superjson from "superjson";
import type { AppRouter } from "cadendar-trpc-server";
import { buildUpdateController } from "./buildUpdate";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (error) => {
        if (error instanceof TRPCClientError) {
          if (error.message === "UNAUTHORIZED") {
            window.location.reload();
          }
        }
        return false;
      },
    },
  },
});

const hostDirty = import.meta.env.VITE_DEV_HOST || window.location.host;
const mode = import.meta.env.MODE;
const prefix = mode === "development" ? "http://" : "https://";
const host = hostDirty.replace("http://", "").replace("https://", "");

export const apiBaseUrl = `${prefix}${host}`;

const url = `${prefix}${host}/cadendarApi`;

export const trpc = createTRPCReact<AppRouter>();

let ownerHeader = "";

export function setTrpcOwner(owner: string | null) {
  ownerHeader = owner || "";
}

export function getTrpcOwner() {
  return ownerHeader;
}

function getBuildNumberFromResponse(response: Response) {
  const headers = response.headers;
  return headers.get("x-build-number");
}

const customLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const unsubscribe = next(op).subscribe({
        next(value) {
          const response = value.context?.response as Response | undefined;
          const serverBuildNumber = response
            ? getBuildNumberFromResponse(response)
            : null;

          buildUpdateController.reportServerBuild(serverBuildNumber);

          observer.next(value);
        },
        error(err) {
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });

      return unsubscribe;
    });
  };
};

function retryDelayMs(attempt: number) {
  const base = 1000;
  const max = 30000;
  const backoff = Math.min(base * 2 ** attempt, max);
  const jitter = Math.floor(Math.random() * backoff);
  return backoff + jitter;
}

export const trpcClient = trpc.createClient({
  links: [
    customLink,
    splitLink({
      condition: (op) => {
        return op.type === "subscription";
      },
      true: [
        retryLink({
          retry: (opts) => {
            const code = opts.error.data?.code;

            if (!code) {
              console.error("No error code found, retrying", opts);
              return true;
            }

            if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
              console.log("Retrying due to 401/403 error");
              return true;
            }

            return false;
          },
          retryDelayMs,
        }),
        httpSubscriptionLink({
          url,
          transformer: superjson,
          eventSourceOptions: { withCredentials: true },
        }),
      ],
      false: httpBatchLink({
        url,
        headers() {
          return {
            "x-cadendar-owner": ownerHeader,
          };
        },
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: "include",
          });
        },
        transformer: superjson,
      }),
    }),
  ],
});
